import { defineStore } from 'pinia'

import type { Product } from '~/models/product'
import type { ApiEnvelope } from '~/types/api'

const STORAGE_KEY = 'favorite-products'

function isProductPayload(v: unknown): v is Product {
  return Boolean(v && typeof v === 'object' && 'id' in v && typeof (v as Product).id === 'number')
}

export const useFavoritesStore = defineStore('favorites', () => {
  const items = ref<Product[]>([])
  const initialized = ref(false)
  let loadPromise: Promise<void> | null = null

  const persistLocal = () => {
    if (import.meta.client) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items.value))
    }
  }

  const loadFromLocalStorage = () => {
    if (!import.meta.client) {
      return
    }
    const raw = localStorage.getItem(STORAGE_KEY)
    items.value = raw ? (JSON.parse(raw) as Product[]) : []
  }

  /**
   * Guests: localStorage only. Logged-in players: GET /api/v1/player/favorites (source of truth).
   */
  const load = async () => {
    if (!import.meta.client) {
      return
    }

    if (initialized.value) {
      return
    }
    if (loadPromise) {
      await loadPromise
      return
    }

    loadPromise = (async () => {
    const { isAuthenticated } = useSanctumAuth()

    if (isAuthenticated.value) {
      try {
        const client = useSanctumClient()
        const res = await client<ApiEnvelope<Product[]>>('/api/v1/player/favorites')
        items.value = Array.isArray(res.data) ? res.data : []
        initialized.value = true
        return
      } catch {
        loadFromLocalStorage()
        initialized.value = true
        return
      }
    }

    loadFromLocalStorage()
    initialized.value = true
    })().finally(() => {
      loadPromise = null
    })

    await loadPromise
  }

  const add = async (product: Product) => {
    if (!import.meta.client) {
      return
    }

    const { isAuthenticated } = useSanctumAuth()

    if (isAuthenticated.value) {
      const client = useSanctumClient()
      const res = await client<ApiEnvelope<Product>>('/api/v1/player/favorites', {
        method: 'POST',
        body: { product_id: product.id },
      })

      const payload = res.data
      if (isProductPayload(payload)) {
        items.value = [payload, ...items.value.filter((item) => item.id !== payload.id)]
      } else {
        await load()
      }
      return
    }

    if (!initialized.value) {
      await load()
    }
    if (items.value.some((item) => item.id === product.id)) {
      return
    }
    items.value.unshift({
      id: product.id,
      model: product.model,
      sku: product.sku,
      display_name: product.display_name,
      name: product.name,
      description: product.description,
      price: product.price,
      quantity: product.quantity,
      status: product.status,
      image: product.image,
      images: product.images,
      product_condition: product.product_condition,
      packaging_condition: product.packaging_condition,
      msrp: product.msrp,
      current_bid: product.current_bid,
      bids_count: product.bids_count,
      auction_starts_at: product.auction_starts_at,
      auction_ends_at: product.auction_ends_at,
    })
    persistLocal()
  }

  const remove = async (productId: number) => {
    if (!import.meta.client) {
      return
    }

    const { isAuthenticated } = useSanctumAuth()

    if (isAuthenticated.value) {
      const client = useSanctumClient()
      await client(`/api/v1/player/favorites/${String(productId)}`, {
        method: 'DELETE',
      })
      items.value = items.value.filter((item) => item.id !== productId)
      return
    }

    if (!initialized.value) {
      await load()
    }
    items.value = items.value.filter((item) => item.id !== productId)
    persistLocal()
  }

  const toggle = async (product: Product) => {
    const { isAuthenticated } = useSanctumAuth()
    if (isAuthenticated.value && !initialized.value) {
      await load()
    }
    const on = items.value.some((item) => item.id === product.id)
    if (on) {
      await remove(product.id)
    } else {
      await add(product)
    }
  }

  const isFavorite = (productId: number) => {
    if (import.meta.client && !initialized.value) {
      loadFromLocalStorage()
      initialized.value = true
    }
    return items.value.some((item) => item.id === productId)
  }

  const count = computed(() => items.value.length)

  return {
    data: {
      items,
    },
    methods: {
      load,
      add,
      remove,
      toggle,
      isFavorite,
    },
    count,
  }
})
